import { BigNumber } from "@yjsgames/bignumber";
import type { BrickCellKind } from "../data/ChapterData.js";

/**
 * 특수 벽돌(방어막/장갑)의 피해 해석 — 순수(Phaser·BigNumber만 의존), 결정론.
 *
 * BattlefieldRunner.damageBrick(직격·폭발·연쇄 단일 chokepoint)이 HP 적용 전 이 함수를 호출해
 * shield 차단 / armor 감산을 결정한다. 셸은 결과대로 shield 소비 또는 finalDamage 적용만 한다(패러다임 규칙 6).
 *
 * - shield & shieldHits>0: 타격 1회가 방어막 1을 소모(피해량 무관). HP 무피해(blockedByShield).
 * - armor: finalDamage = max(1, incoming − armor). 최소 1 보정으로 완전 차단은 없음(진행 보장).
 * - 그 외(normal 등): 입력 피해 그대로.
 */

export interface BrickDamageState {
  readonly kind: BrickCellKind;
  /** 남은 방어막 타격 횟수 (shield 벽돌만 >0). */
  readonly shieldHits: number;
  /** 고정 방어력 (armor 벽돌만 >0, 챕터 비례 BigNumber). */
  readonly armor: BigNumber;
}

export interface BrickDamageResult {
  /** true면 방어막이 이 타격을 막음 — HP·골드·파괴 처리 생략. */
  readonly blockedByShield: boolean;
  /** 차단 시 1 감소, 그 외 입력 유지. */
  readonly shieldHitsAfter: number;
  /** HP에 실제 적용할 피해(armor 반영, blockedByShield면 0). */
  readonly finalDamage: BigNumber;
}

/**
 * 피해 해석. 방어막 차단(우선) → 장갑 감산(최소 1) → 일반 순.
 * shield 차단은 피해량과 무관하게 항상 방어막 1만 소모(기획 §4 "shieldHits 감소는 피해량과 무관").
 */
export function resolveBrickDamage(
  state: BrickDamageState,
  incoming: BigNumber,
): BrickDamageResult {
  if (state.kind === "shield" && state.shieldHits > 0) {
    return {
      blockedByShield: true,
      shieldHitsAfter: state.shieldHits - 1,
      finalDamage: BigNumber.ZERO,
    };
  }
  if (state.kind === "armor" && state.armor.gt(BigNumber.ZERO)) {
    return {
      blockedByShield: false,
      shieldHitsAfter: state.shieldHits,
      finalDamage: incoming.sub(state.armor).max(BigNumber.ONE),
    };
  }
  return {
    blockedByShield: false,
    shieldHitsAfter: state.shieldHits,
    finalDamage: incoming,
  };
}
